import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with default config
const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout for regular requests
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.code === 'ECONNABORTED') {
      error.message = 'Request timed out. Please try again.';
    }
    return Promise.reject(error);
  }
);

// ============ COLLECTIONS ============
export const collectionsAPI = {
  getAll: async () => {
    const response = await api.get('/collections');
    return response.data;
  },
  
  getById: async (id) => {
    const response = await api.get(`/collections/${id}`);
    return response.data;
  },
  
  getBySlug: async (slug) => {
    const response = await api.get(`/collections/slug/${slug}`);
    return response.data;
  },
  
  create: async (data) => {
    const response = await api.post('/collections', data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await api.put(`/collections/${id}`, data);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await api.delete(`/collections/${id}`);
    return response.data;
  },
};

// ============ ARTWORKS ============
export const artworksAPI = {
  getAll: async (collectionId = null) => {
    const params = collectionId ? { collection_id: collectionId } : {};
    const response = await api.get('/artworks', { params });
    return response.data;
  },
  
  getById: async (id) => {
    const response = await api.get(`/artworks/${id}`);
    return response.data;
  },
  
  getBySlug: async (slug) => {
    const response = await api.get(`/artworks/slug/${slug}`);
    return response.data;
  },
  
  create: async (data) => {
    const response = await api.post('/artworks', data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await api.put(`/artworks/${id}`, data);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await api.delete(`/artworks/${id}`);
    return response.data;
  },
};

// ============ BLOG POSTS ============
export const blogAPI = {
  getAll: async () => {
    const response = await api.get('/blog');
    return response.data;
  },
  
  getById: async (id) => {
    const response = await api.get(`/blog/${id}`);
    return response.data;
  },
  
  getBySlug: async (slug) => {
    const response = await api.get(`/blog/slug/${slug}`);
    return response.data;
  },
  
  create: async (data) => {
    const response = await api.post('/blog', data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await api.put(`/blog/${id}`, data);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await api.delete(`/blog/${id}`);
    return response.data;
  },
};

// ============ SETTINGS ============
export const settingsAPI = {
  get: async () => {
    const response = await api.get('/settings');
    return response.data;
  },
  
  update: async (data) => {
    const response = await api.put('/settings', data);
    return response.data;
  },
};

// ============ STATS ============
export const statsAPI = {
  get: async () => {
    const response = await api.get('/stats');
    return response.data;
  },
};

// ============ USERS ============
export const usersAPI = {
  getAll: async () => {
    const response = await api.get('/users');
    return response.data;
  },
  
  create: async (data) => {
    const response = await api.post('/users', data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await api.put(`/users/${id}`, data);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await api.delete(`/users/${id}`);
    return response.data;
  },
  
  changePassword: async (currentPassword, newPassword) => {
    const response = await api.put('/users/change-password/me', {
      current_password: currentPassword,
      new_password: newPassword
    });
    return response.data;
  },
  
  resetPassword: async (userId) => {
    const response = await api.put(`/users/${userId}/reset-password`);
    return response.data;
  },
};

// ============ FILE UPLOAD ============
export const uploadAPI = {
  upload: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 120000, // 2 minute timeout for uploads
    });
    return response.data;
  },
};

// ============ MEDIA LIBRARY ============
export const mediaAPI = {
  getAll: async () => {
    const response = await api.get('/media');
    return response.data;
  },
  
  delete: async (id) => {
    const response = await api.delete(`/media/${id}`);
    return response.data;
  },
};

// ============ CHECKOUT & ORDERS ============
export const checkoutAPI = {
  calculateShipping: async (items, countryCode) => {
    const response = await api.post('/checkout/calculate-shipping', items, {
      params: { country_code: countryCode }
    });
    return response.data;
  },
  
  createSession: async (checkoutData) => {
    const response = await api.post('/checkout/create-session', checkoutData);
    return response.data;
  },
  
  getStatus: async (sessionId) => {
    const response = await api.get(`/checkout/status/${sessionId}`);
    return response.data;
  },
  
  getShippingRates: async () => {
    const response = await api.get('/shipping-rates');
    return response.data;
  },
  
  getCurrencies: async () => {
    const response = await api.get('/currencies');
    return response.data;
  },
};

export const ordersAPI = {
  getAll: async () => {
    const response = await api.get('/orders');
    return response.data;
  },
  
  getById: async (id) => {
    const response = await api.get(`/orders/${id}`);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await api.patch(`/orders/${id}`, data);
    return response.data;
  },
};

export const quotesAPI = {
  create: async (data) => {
    const response = await api.post('/quotes', data);
    return response.data;
  },
  
  getAll: async () => {
    const response = await api.get('/quotes');
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await api.patch(`/quotes/${id}`, data);
    return response.data;
  },
};

// ============ CONTACT MESSAGES ============
export const messagesAPI = {
  getAll: async () => {
    const response = await api.get('/contact/messages');
    return response.data;
  },
  
  updateStatus: async (id, status) => {
    const response = await api.patch(`/contact/messages/${id}?status=${status}`);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await api.delete(`/contact/messages/${id}`);
    return response.data;
  },
};

// ============ SEED ============
export const seedDatabase = async () => {
  const response = await api.post('/seed');
  return response.data;
};

export default api;
